import { useCallback, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { useMutation } from '@apollo/client'
import clsx from 'clsx'
import copy from 'copy-to-clipboard'
import useTracking from '@/lib/tracking/useTracking'
import useToast from '@/hooks/useToast'
import useLiveInfo from '@/hooks/useLiveInfo'
import useTrackingInfo from '@/hooks/useTrackingInfo'
import { Coupon as CouponItem, IssueType } from '@/utils/types'
import RedeemCouponMutation from './gql/RedeemCouponMutation.gql'
import styles from './index.module.sass'
import CouponToast from './components/CouponToast'

interface Props {
  coupon: CouponItem
  onRefresh?: () => void
}

const Coupon = ({ coupon, onRefresh }: Props) => {
  const { t } = useTranslation()
  const isReachTheLimitation = useMemo(
    () =>
      coupon?.maxCouponsPerUser > 0 &&
      coupon?.received === coupon?.maxCouponsPerUser,
    [coupon?.received, coupon?.maxCouponsPerUser]
  )

  const { broadcastId, shopId } = useLiveInfo()

  const { push } = useToast()

  const [redeemCall, { loading }] = useMutation(RedeemCouponMutation, {
    variables: {
      broadcastId,
      policyId: coupon.policyId
    },
    onCompleted () {
      push({
        message: t('client.coupons.toast.issuesuccess'),
        Component: CouponToast,
        customProps: {
          imageUrl: '/img/svg/ico-check--white.svg'
        },
        duration: 4000
      })
      onRefresh?.()
    },
    onError (error) {
      let message = ''
      switch ((error.graphQLErrors?.[0] as any)?.errorCode) {
        case 'REACH_MAX_COUNT':
          message = t('client.coupons.toast.runout')
          break
        case 'REACH_PERSONAL_LIMIT_COUNT':
          message = t('client.coupons.toast.maxreached')
          break
        case 'EXPIRED':
          message = t('client.coupons.toast.expired')
          break
        case 'UNAVAILABLE_STAT':
          message = t('client.coupons.toast.unavailable')
          break
        case 'UNABLE_CREATE':
          message = t('client.coupons.toast.serviceerror')
          break
        default:
          message = t('client.coupons.toast.serviceerror')
      }
      push({
        message,
        Component: CouponToast,
        duration: 4000
      })
      onRefresh?.()
    }
  })

  const copyCouponCode = useCallback(() => {
    if (!coupon) return

    if (copy(coupon.policyId)) {
      push({
        message: t('client.coupons.toast.copied'),
        Component: CouponToast,
        customProps: {
          imageUrl: '/img/svg/ico-check--white.svg'
        },
        duration: 4000
      })
    }
  }, [t, coupon, push])

  const isLocked = useMemo(
    () => loading || !coupon.available || isReachTheLimitation,
    [loading, coupon?.available, isReachTheLimitation]
  )

  const isCopyType = useMemo(
    () => coupon.issueType === IssueType.COPY_BY_POLICY_ID,
    [coupon]
  )

  const { screenName } = useTrackingInfo()
  const { tracker } = useTracking()
  const redeemCoupon = useCallback(() => {
    if (
      isLocked ||
      !broadcastId ||
      !coupon?.policyId
    ) { return }

    if (isCopyType) {
      copyCouponCode()
    } else {
      redeemCall()
    }

    tracker?.event({
      config: { utsType: 'click' },
      event: {
        screenName,
        category: 'coupon_list',
        params: {
          target: 'download_coupon',
          broadcast_id: broadcastId,
          shop_id: shopId,
          coupon_id: coupon.policyId
        }
      }
    })
  }, [isLocked, screenName, broadcastId, coupon?.policyId, redeemCall, isCopyType, copyCouponCode, tracker, shopId])

  return (
    <div className={styles.coupon}>
      <div className={styles.topBlock}>
        <div className={styles.info}>
          <div className={styles.name}>{coupon.name}</div>
          <div className={styles.rules}>
            {coupon.minOrderAmount || coupon.maxDiscountAmount
              ? (
                <div className={styles.rulesContent}>
                  <span>{coupon.minOrderAmount}</span>
                  {coupon.minOrderAmount && coupon.maxDiscountAmount ? <span className={styles.rulesDot}>•</span> : null}
                  <span>{coupon.maxDiscountAmount}</span>
                </div>
                )
              : null}
            <div className={styles.rulesDate}>
              {`${t('client.coupons.desc.validuntil')}: ${coupon.validityPeriod}`}
            </div>
          </div>
        </div>
        <div
          className={clsx(
            styles.status,
            isLocked ? styles.disabled : ''
          )}
          onClick={isLocked ? undefined : redeemCoupon}
        >
          {coupon.available || isReachTheLimitation
            ? (
              <span
                className={
                  isReachTheLimitation
                    ? styles.iconCheck
                    : isCopyType
                      ? styles.iconCopy
                      : styles.iconDownload
                }
              >
                { isCopyType ? 'Copy' : '' }
              </span>
              )
            : <span className={styles.buttonText}>{t('client.coupons.desc.outofstock')}</span>}
        </div>
      </div>
      <div className={styles.receiveInfo}>
        {
          coupon.remaining !== null && coupon.remaining >= -1 && <span>
            {`${t('client.coupons.desc.remaining')}: ${coupon.remaining === -1 ? t('client.coupons.desc.unlimited') : coupon.remaining}`}
          </span>
        }
        {
          coupon.remaining === null && <span>
            {`${t('client.coupons.desc.maximum')}: ${coupon.maxCouponsPerUser === -1 ? t('client.coupons.desc.unlimited') : coupon.maxCouponsPerUser}`}
          </span>
        }
        {
          coupon.remaining === null && !isCopyType &&
          <>
            <span className={styles.rulesDot}>•</span>
            <span className={coupon.received > 0 ? '' : styles.textGrey}>
              {`${t('client.coupons.desc.issued')}: ${coupon.received}`}
            </span>
          </>
        }
      </div>
    </div>
  )
}

Coupon.defaultProps = {
  coupon: {},
  onRefresh: undefined
}

export default Coupon
